import { Hotel, TrendingUp, Utensils, UtensilsCrossed, Wine } from "lucide-react";
import React from "react";
import { Card } from "../../components/ui/Card";
import { Badge } from "../../components/ui/Badge";
import Chart from "react-apexcharts";
import { ApexOptions } from "apexcharts";

const EventsMarketing = () => {
  const stats = [
    {
      name: "Restaurant traditionnel",
      value: 155,
      icon: <Utensils className="h-6 w-6 text-white" />,
      dotColor: "bg-accent",
    },
    {
      name: "Restauration rapide",
      value: 469,
      icon: <UtensilsCrossed className="h-6 w-6 text-white" />,
      dotColor: "bg-info",
    },
    {
      name: "Hôtels et hébergements",
      value: 40,
      icon: <Hotel className="h-6 w-6 text-white" />,
      dotColor: "bg-success",
    },
    {
      name: "Débit de boisson",
      value: 62,
      icon: <Wine className="h-6 w-6 text-white" />,
      dotColor: "bg-warning",
    },
  ];

  const total = stats.reduce((acc, curr) => acc + curr.value, 0);

  const pieChartOptions: ApexOptions = {
    labels: stats.map(s => s.name),
    colors: ["#E35F2A", "#3A6EA5", "#2F8F5B", "#B7791F"],
    chart: {
      width: "100%",
    },
    states: {
      hover: {
        filter: {
          type: "none",
        },
      },
    },
    legend: {
      show: false,
    },
    dataLabels: {
      enabled: false,
    },

    plotOptions: {
      pie: {
        expandOnClick: false,
        donut: {
          labels: {
            show: false,
          },
        },
      },
    },
    fill: {
      colors: ["#E35F2A", "#3A6EA5", "#2F8F5B", "#B7791F"],
    },
    tooltip: {
      enabled: true,
      theme: "dark",
    },
  };

  const pieChartData = stats.map(s => s.value);

  const _topCities = [
    { name: "Paris", value: 85, percent: 85 },
    { name: "Lyon", value: 42, percent: 50 },
    { name: "Marseille", value: 38, percent: 45 },
    { name: "Bordeaux", value: 25, percent: 30 },
    { name: "Lille", value: 20, percent: 25 },
  ];

  return (
    <div className="flex w-full flex-col gap-6 p-4 md:p-12 bg-canvas min-h-screen font-sans">
      {/* Header / Hero */}
      <div className="relative overflow-hidden rounded-2xl bg-surface-inverse p-8">
        <div className="absolute top-0 right-0 -mt-10 -mr-10 h-64 w-64 rounded-full bg-accent/10 blur-3xl"></div>
        <div className="absolute bottom-0 left-0 -mb-10 -ml-10 h-64 w-64 rounded-full bg-white/5 blur-3xl"></div>

        <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="text-left">
            <div className="inline-flex items-center gap-2 rounded-full bg-accent-subtle-inverse px-3 py-1 text-xs font-medium text-accent mb-3">
              <TrendingUp className="h-3.5 w-3.5" />
              <span>Rapport Mensuel</span>
            </div>
            <h1 className="font-display text-3xl font-normal text-ink-inverse md:text-5xl tracking-tight leading-tight">
              Ouvertures CHR
            </h1>
            <p className="mt-2 text-lg text-ink-inverse-muted font-medium">
              Novembre 2025 • France
            </p>
          </div>

          <div className="flex flex-col items-end">
            <div className="flex items-baseline gap-2">
                <span className="font-sans text-6xl font-semibold text-ink-inverse tracking-tight">{total}</span>
                <span className="text-xl font-medium text-ink-inverse-muted">nouvelles</span>
            </div>
            <span className="text-lg text-ink-inverse-muted">entreprises détectées</span>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Left Column: Stats Cards */}
        <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {stats.map((stat, index) => (
                <div key={index} className="rounded-2xl border border-border bg-surface p-5 flex flex-col justify-between hover:border-border-strong transition-colors duration-150">
                    <div className="flex items-start justify-between">
                        <div className="p-3 rounded-full bg-accent">
                            {stat.icon}
                        </div>
                        <Badge tone="neutral">
                            {((stat.value / total) * 100).toFixed(1)}%
                        </Badge>
                    </div>
                    <div className="mt-4">
                        <h4 className="font-sans text-3xl font-semibold tracking-tight text-ink">
                            {stat.value}
                        </h4>
                        <p className="text-sm font-medium text-ink-muted mt-1">
                            {stat.name}
                        </p>
                    </div>
                </div>
            ))}
        </div>

        {/* Right Column: Charts & Top Cities */}
        <div className="flex flex-col gap-6">
            {/* Distribution Chart */}
            <Card className="flex flex-col items-center justify-center">
                <h3 className="text-sm font-semibold text-ink w-full text-left mb-4">
                    Répartition par activité
                </h3>
                <div className="w-full flex items-center justify-center min-h-[200px]">
                    <Chart
                        options={pieChartOptions}
                        series={pieChartData}
                        type="donut"
                        width="100%"
                        height="250px"
                    />
                </div>
                <div className="w-full mt-4 space-y-2">
                    {stats.map((stat, i) => (
                        <div key={i} className="flex items-center justify-between text-sm">
                            <div className="flex items-center gap-2">
                                <div className={`w-3 h-3 rounded-full ${stat.dotColor}`}></div>
                                <span className="text-ink-muted">{stat.name}</span>
                            </div>
                            <span className="font-semibold text-ink">{((stat.value / total) * 100).toFixed(0)}%</span>
                        </div>
                    ))}
                </div>
            </Card>
        </div>
      </div>

      {/* Footer Branding */}
      <div className="flex justify-center mt-4">
        <p className="text-sm text-ink-faint font-medium flex items-center gap-2">
            Données générées par <span className="text-accent font-semibold">DuckFlow Intelligence</span>
        </p>
      </div>
    </div>
  );
};

export default EventsMarketing;
